import React, { useEffect, useMemo, useState } from "react";
import { CustomDropdown } from "../atoms";
import { useGetTaskAssigneesQuery } from "../../store";
import useAuth from "../../Hooks/useAuth";
import UserChip from "./atoms/UserChip";
import { userName } from "./taskConstants";

export const ROLES = [
  { id: "manager", label: "Yönetici" },
  { id: "member", label: "Üye" },
];
export const LAST_MANAGER_MESSAGE = "Projede en az bir yönetici olmalı.";

const sameList = (a, b) =>
  a.length === b.length && a.every((m, i) => m.user._id === b[i].user._id && m.role === b[i].role);

// Proje üyeleri: rol seç, çıkar, kişi ekle. Kaydet -> onSave(members: {user, role}[])
// (tam liste). Son yöneticinin rolü düşürülemez / çıkarılamaz.
function ProjectMembersEditor({ members = [], onSave, saving = false, disabled = false, onDirtyChange }) {
  const { auth } = useAuth();
  const [draft, setDraft] = useState(members);
  const [search, setSearch] = useState("");
  const { data, isFetching } = useGetTaskAssigneesQuery({ token: auth?.accessToken, search });

  const managerCount = draft.filter((m) => m.role === "manager").length;
  const dirty = !sameList(draft, members);

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => onDirtyChange?.(false), []); // eslint-disable-line react-hooks/exhaustive-deps

  const addOptions = useMemo(
    () =>
      (data?.data?.users || [])
        .filter((u) => !draft.some((m) => m.user._id === u._id))
        .map((u) => ({ id: u._id, label: userName(u), user: u })),
    [data, draft]
  );

  const setRole = (id, role) =>
    setDraft((cur) => cur.map((m) => (m.user._id === id ? { ...m, role } : m)));
  const remove = (id) => setDraft((cur) => cur.filter((m) => m.user._id !== id));
  const add = (o) => o && setDraft((cur) => [...cur, { user: o.user, role: "member" }]);

  return (
    <div data-testid="members-editor">
      <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200">
        {draft.map((m) => {
          const lastManager = m.role === "manager" && managerCount <= 1;
          const locked = disabled || saving;
          return (
            <li key={m.user._id} className="flex items-center justify-between gap-3 px-3 py-2">
              <div className="min-w-0">
                <UserChip user={m.user} size="sm" />
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <select
                  aria-label={`${userName(m.user)} rolü`}
                  value={m.role}
                  disabled={lastManager || locked}
                  title={lastManager ? LAST_MANAGER_MESSAGE : undefined}
                  onChange={(e) => setRole(m.user._id, e.target.value)}
                  className="h-8 rounded-lg border border-gray-200 bg-white px-2 text-sm text-gray-700 disabled:bg-gray-50 disabled:text-gray-400"
                >
                  {ROLES.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  aria-label={`${userName(m.user)} üyeliğini çıkar`}
                  disabled={lastManager || locked}
                  title={lastManager ? LAST_MANAGER_MESSAGE : undefined}
                  onClick={() => remove(m.user._id)}
                  className="text-sm font-Medium text-gray-500 hover:text-fifth disabled:opacity-40 disabled:hover:text-gray-500"
                >
                  Çıkar
                </button>
              </div>
            </li>
          );
        })}
        {draft.length === 0 && <li className="px-3 py-3 text-sm text-gray-400">Henüz üye yok.</li>}
      </ul>
      {managerCount <= 1 && (
        <p className="mt-2 text-xs text-gray-500" data-testid="manager-hint">
          {LAST_MANAGER_MESSAGE}
        </p>
      )}

      <div className="mt-3">
        <CustomDropdown
          compact
          disabled={disabled}
          options={addOptions}
          loading={isFetching}
          onSearch={setSearch}
          placeholder="Üye ekle..."
          searchPlaceholder="Kişi ara..."
          value={null}
          onChange={add}
        />
      </div>

      <div className="mt-3 flex justify-end">
        <button
          type="button"
          disabled={!dirty || saving || disabled || managerCount < 1}
          onClick={() => onSave(draft)}
          className="px-4 py-1.5 rounded-lg text-sm font-Medium bg-main text-white hover:opacity-90 disabled:opacity-50"
        >
          {saving ? "Kaydediliyor..." : "Üyeleri Kaydet"}
        </button>
      </div>
    </div>
  );
}

export default ProjectMembersEditor;
